// Follows the executions of efficiency tests that are under way, for a component that lists them.
//
// An execution is asynchronous: the "execute" request answers at once with the operation to follow, and the
// steps - checking the comparables, importing the secondary market certificates, calibrating, scoring,
// saving - are read by polling it (utils/operationPoller.js). A test that is already in progress when the
// list is loaded (the page was reloaded, or the execution was started elsewhere) carries the operation in
// `activeOperationId`, and is followed from there on.
//
// Nothing is told when it ends: the test is the source of truth for how it ended, so the host reloads what
// it shows (`onEnded`) and reads the outcome, and the failures, from there.
//
// `progressOf(testId)` is reactive: a template that calls it is redrawn as the operation moves.
import Vue from 'vue'
import { get } from './fetcher'
import { startPolling } from './operationPoller'

/**
 * @param {{ onEnded: Function }} options `onEnded`: called when an execution ends, and when it can no longer
 *   be followed (the server lost it, or it outlasted the following).
 */
export function createExecutionFollower({ onEnded }) {
  // The object is replaced, not added to, at each change: that is what a template that reads it depends on
  // (a key that is not there yet has no dependency of its own).
  const state = Vue.observable({ byTest: {} })
  const pollers = {}
  // Operations that were followed until the timeout, by operation id -> test id: not taken up again by the
  // next load of the list, but followed again when the user comes back to the page (see `resume`).
  let abandoned = {}

  function forget(testId) {
    if (pollers[testId]) {
      pollers[testId].stop()
      delete pollers[testId]
    }
    const rest = { ...state.byTest }
    delete rest[testId]
    state.byTest = rest
  }

  function end(testId) {
    forget(testId)
    onEnded()
  }

  function isTracking(testId) {
    return !!pollers[testId]
  }

  // No operation (its progress could not be recorded) means nothing to follow: the status of the test,
  // reloaded by the host, is all there is.
  function track(testId, operationId) {
    if (!operationId || isTracking(testId)) {
      return
    }

    pollers[testId] = startPolling('efficiencyTestRun', () => get('/api/operations/' + operationId), {
      onUpdate: (update) => {
        state.byTest = { ...state.byTest, [testId]: update }
      },
      onFinish: () => end(testId),
      onNotFound: () => end(testId),
      onTimeout: () => {
        abandoned[operationId] = testId
        end(testId)
      },
    })
  }

  function adopt(items) {
    ;(items || []).forEach((item) => {
      const testId = item.scoringProfileId || item.id
      if ((item.status === 'InProgress' || item.status === 'WaitingResources') && item.activeOperationId && !abandoned[item.activeOperationId]) {
        track(testId, item.activeOperationId)
      }
    })
  }

  // The user is back: what was given up on may have ended meanwhile, and the first poll says so at once.
  // A test still running is followed for one more timeout.
  function resume() {
    if (typeof document !== 'undefined' && document.hidden) {
      return
    }
    const given = abandoned
    abandoned = {}
    Object.keys(given).forEach((operationId) => track(given[operationId], operationId))
  }

  function stopAll() {
    document.removeEventListener('visibilitychange', resume)
    window.removeEventListener('focus', resume)
    Object.keys(pollers).forEach((testId) => pollers[testId].stop())
    Object.keys(pollers).forEach((testId) => delete pollers[testId])
  }

  document.addEventListener('visibilitychange', resume)
  window.addEventListener('focus', resume)

  return {
    progressOf: (testId) => state.byTest[testId] || null,
    isTracking,
    track,
    adopt,
    stopAll,
  }
}
